/* global BRAND */
import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import moment from 'moment'
import { registerField, formValueSelector } from 'redux-form'
import { translate, Storage } from 'loankeeper-common/helpers'
import { LanguageService } from 'loankeeper-common/helpers/language'
import { loadFont } from 'loankeeper-common/helpers/styles'
import IconPreloader from 'loankeeper-common/components/IconPreloader'
import { LKIcon, LKButton } from 'loankeeper-common/components'
import { DocumentTitle } from 'shared'
import { getSession } from '../../../store/auth/auth.selectors'
import { forgetAboutBuildUpdate, getNeededBuildUpdate } from '../../../store/persist'
import FormField from '../../shared/newFormElements'
import CapsLockStateProvider from './CapsLockStateProvider'
import { withNetworkState } from '../useNetworkState'
import containerForm from './reduxForm'
import { isOTPValid } from '../selectors'
import { FORM_NAME } from '../constants'
import { withUnauthGuard } from '../../../common/authGuards'
import AuthChangePasswordModal from '../../ChangePasswordForm/AuthChangePasswordModal'
import { SendLoginPinButton } from './SendLoginPinButton'
import { LoginProps, LoginState } from '../Auth.types'
import PasswordResetForm from '../PasswordResetForm'
import {
  normalizeOTP,
  normalizeUsername,
  normalizePassword,
  passwordPattern,
  usernamePattern,
} from '../PasswordResetForm/container'

//@ts-ignore
const LoginLayout = require(`../../../../brands/${BRAND}/components/LoginLayout`).default // eslint-disable-line
//@ts-ignore
let chosenLang = Storage.get('currentLang')

LanguageService.setLanguage(chosenLang || process.env.LANG_LOCALES[0].toLowerCase())

const translateMain = (key: string): string => translate(`frm_web_messages-${key}`)
const translatePasswordRecovery = (key: string): string => translate(`frm_password_recovery-${key}`)
const translateForm = (key: string): string => translate(`frm_login-${key}`)

type LogginButtonProps = {
  theme: {
    buttonFont: string
  }
}

const LoginButton = styled(LKButton)`
  .ui.button {
    ${(props: LogginButtonProps) => loadFont(props.theme.buttonFont || '13px BPG Algeti')};
  }
`

const StyledLKIcon = styled(LKIcon)`
  &.lk-icon {
    height: 28px !important;
    width: 20px;
    position: absolute;
    left: 10px;
    top: 2px;
  }
`
const SmsSendContainer = styled.div`
  display: flex;
  margin-bottom: ${(props: { showMargins: boolean }) => (props.showMargins ? '20px' : '0')};
`

const PasswordResetLink = styled.span`
  color: #15aff4;
  text-decoration: underline;
  cursor: pointer;
`

//@ts-ignore
@withUnauthGuard
//@ts-ignore
@withNetworkState
@containerForm
//@ts-ignore
@connect(
  (state) => ({
    otpValid: isOTPValid(state, formValueSelector(FORM_NAME)(state, 'username') || ''),
    session: getSession(state),
    didHashUpdated: getNeededBuildUpdate(state),
  }),
  {
    forgetAboutBuildUpdate,
  },
)
class Login extends React.PureComponent<LoginProps, LoginState> {
  state = {
    passwordWarning: false,
    usernameWarning: false,
    didHashUpdated: this.props.didHashUpdated,
    isCapsLockOn: false,
    showPasswordResetForm: false,
  }

  componentDidMount() {
    const { STK, username, STKExpire } = this.props.session

    if (STK && moment(STKExpire).isAfter(moment())) {
      this.props.history.replace('/secure')
    } else {
      this.props.initialize({ username })
      this.props.dispatch(registerField(this.props.form, 'otp', 'Field'))
      this.props.forgetAboutBuildUpdate()
    }
  }

  togglePasswordResetForm() {
    this.setState((prevState) => ({
      showPasswordResetForm: !prevState.showPasswordResetForm,
    }))
  }

  onCheckCapsLock = (isOn: boolean) => {
    const { isCapsLockOn: wasCapsLockOn } = this.state

    if (wasCapsLockOn !== isOn) {
      this.setState({ isCapsLockOn: isOn })
    }
  }

  usernameValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hasUsernameWarning = usernamePattern.test(e.target.value)
    this.setState({ usernameWarning: !hasUsernameWarning })
  }

  passwordValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hasPasswordWarning = passwordPattern.test(e.target.value)
    this.setState({ passwordWarning: !hasPasswordWarning })
  }

  handleSubmit = (event: React.SyntheticEvent) => {
    const { isOnline } = this.props

    if (!isOnline) {
      event.preventDefault()
      return
    }

    this.props.handleSubmit(event)
  }

  renderError = (key: string, message: string, withWarningIcon = true) => (
    <div
      key={key}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {withWarningIcon && <LKIcon alias="alert" />}
      <span style={{ color: '#DC403B', marginLeft: 5 }}>{message}</span>
    </div>
  )

  render() {
    const { didHashUpdated, isCapsLockOn, passwordWarning, showPasswordResetForm, usernameWarning } = this.state
    const { pristine, session, submitting, otpValid, isOnline, resetFormValues } = this.props

    const hashErrorMessage = didHashUpdated ? translateMain('MSG_VERSION_UPDATED') : null

    const errors = [
      !isOnline && this.renderError('ONLINE_STATUS', translateMain('ERR_NO_CONNECTION'), false),
      session.msg && this.renderError('MESSAGE', session.msg),
      hashErrorMessage && this.renderError('HASH_ERROR', hashErrorMessage),
      isCapsLockOn && this.renderError('CAPS_LOCK', 'Caps Lock'),
    ].filter(Boolean)

    return (
      <>
        <DocumentTitle title={translateMain('cmd_log_in')} />
        <CapsLockStateProvider on={isCapsLockOn} onChange={this.onCheckCapsLock} />
        <LoginLayout
          handleSubmit={this.handleSubmit}
          loading={submitting}
          passwordResetForm={
            <PasswordResetForm
              goToAuth={() => {
                resetFormValues()
                this.setState(() => ({
                  showPasswordResetForm: false,
                }))
              }}
              renderError={this.renderError}
            />
          }
          showPasswordResetForm={showPasswordResetForm}
          translate={translateForm}
        >
          <img src="/static/img/test_logo.png" alt="about logo" />
          {errors}
          {/* @ts-ignore */}
          <FormField
            name="username"
            icon={<StyledLKIcon alias="client" />}
            iconPosition="left"
            autoComplete="new-username"
            label={{ icon: usernameWarning ? 'warning sign' : '', className: 'input-second-icon--label' }}
            normalize={normalizeUsername}
            placeholder={translateForm('lbl_user')}
            onChange={this.usernameValidation}
            noErrorPopup
          />

          {/* @ts-ignore */}
          <FormField
            name="password"
            type="password"
            icon={<StyledLKIcon alias="key" />}
            iconPosition="left"
            placeholder={translateForm('lbl_password')}
            autoComplete="new-password"
            label={{ icon: passwordWarning ? 'warning sign' : '', className: 'input-second-icon--label' }}
            labelPosition="right"
            onChange={this.passwordValidation}
            normalize={normalizePassword}
            className="w-pos-relative"
            noErrorPopup
          />

          <SmsSendContainer showMargins={!otpValid}>
            {!otpValid && (
              <>
                {/* @ts-ignore */}
                <FormField
                  name="otp"
                  icon={<StyledLKIcon alias="sms" />}
                  iconPosition="left"
                  placeholder={translateForm('lbl_code')}
                  normalize={normalizeOTP}
                  required
                  autoComplete="off"
                  noErrorPopup
                />
                <SendLoginPinButton disabled={pristine || submitting} text={translateMain('MSG_SMS_OTP_GET')} />
              </>
            )}
          </SmsSendContainer>

          <LoginButton
            name="submit"
            type="primary"
            loading={submitting}
            disabled={pristine || submitting}
            isSubmit
            fluid
          >
            {translateMain('cmd_log_in')}
            {/* {translations.login} */}
          </LoginButton>
          <PasswordResetLink
            onClick={() => {
              this.togglePasswordResetForm()
              resetFormValues()
            }}
          >
            {showPasswordResetForm
              ? translatePasswordRecovery('cmd_auth')
              : translatePasswordRecovery('cmd_recover_password')}
          </PasswordResetLink>
        </LoginLayout>
        <IconPreloader collection="general" />
        <AuthChangePasswordModal />
      </>
    )
  }
}

export default Login
